/**
 * Immutable helpers for auth-entry J-codes: flattening, document add/remove/rename,
 * and auth-letter review flags.
 *
 * @module updateJcodesWithCorrectDocRef
 */
import { v4 as uuidv4 } from "uuid";
import {
  AuthRangeResponse,
  JCodeDetails,
  JCodeDocuments,
} from "../data-model/medicalPaOrdersModel";
import { DocumentTypesForOrder } from "../enums/documentTypes";

/**
 * Flattens all J-codes from auth entries into a single array.
 *
 * @param authEntries - Array of auth range responses containing J-codes.
 * @returns Flat array of every JCodeDetails across all entries.
 */
export const flattenAllJCodes = (
  authEntries: AuthRangeResponse[],
): JCodeDetails[] => {
  return authEntries.flatMap((entry) => entry.j_codes ?? []);
};

/**
 * Sets auth_letter_review_status on J-codes whose id is in jCodeIds.
 */
export const setAuthLetterReviewStatusOnJCodes = (
  authEntries: AuthRangeResponse[],
  jCodeIds: string[],
  status: string,
): AuthRangeResponse[] => {
  const idSet = new Set(jCodeIds);
  return authEntries.map((authEntry) => ({
    ...authEntry,
    j_codes: authEntry.j_codes?.map((jCode) => {
      const key = jCode.id ?? jCode.j_code ?? "";
      return idSet.has(key)
        ? { ...jCode, auth_letter_review_status: status }
        : jCode;
    }),
  }));
};

/**
 * Extracts the `auth_letter_review_status` for every J-code across all auth entries.
 *
 * @param authEntries - Array of auth range responses containing J-codes.
 * @returns Array of `{ id, auth_letter_review_status }` for each J-code.
 */
export const getAuthLetterReviewStatusPerJCode = (
  authEntries: AuthRangeResponse[],
): { id: string; auth_letter_review_status: string }[] => {
  return authEntries.flatMap(
    (entry) =>
      entry.j_codes?.map((jCode) => ({
        id: jCode.id ?? jCode.j_code ?? "",
        auth_letter_review_status: jCode.auth_letter_review_status ?? "",
      })) ?? [],
  );
};

/**
 * Builds minimal J-code rows to clear `auth_letter_review_status` when deleting an
 * auth letter document by its document `id` (e.g. from the documents tab).
 *
 * @param authEntries - Auth-on-file entries before the delete mutation
 * @param docId - Document `id` being removed
 * @returns One `{ id, auth_letter_review_status: "" }` per affected J-code; empty when not an auth letter
 */
export const getAuthLetterReviewStatusClearsForDocId = (
  authEntries: AuthRangeResponse[],
  docId: string,
): { id: string; auth_letter_review_status: "" }[] => {
  if (!docId) return [];

  return authEntries.flatMap(
    (entry) =>
      entry.j_codes?.flatMap((jCode) => {
        const removedDoc = jCode.documents?.find((doc) => doc.id === docId);
        if (removedDoc?.doc_type !== DocumentTypesForOrder.AUTH_LETTER) {
          return [];
        }
        const id = jCode.id ?? jCode.j_code ?? "";
        return id ? [{ id, auth_letter_review_status: "" as const }] : [];
      }) ?? [],
  );
};

/**
 * Updates J-codes with a new document reference by adding a new document to matching J-codes.
 * @param authEntries - Array of auth range responses containing J-codes
 * @param identifier - Array of J-code IDs to match and update
 * @param docType - The type of the document being added
 * @param docRef - The document path/reference
 * @param docTitle - The title of the document
 * @param docUploadBy - The user who uploaded the document
 * @returns Updated array of auth range responses with the new document added to matching J-codes
 */
export const updateJcodesWithCorrectDocRef = (
  authEntries: AuthRangeResponse[],
  identifier: string[],
  docType: string,
  docRef: string,
  docTitle: string,
  docUploadBy: string,
) => {
  const updatedJCodes = authEntries.map((authEntry) => {
    return {
      ...authEntry,
      j_codes: authEntry.j_codes?.map((jCode) => {
        if (identifier.includes(jCode.id ?? "")) {
          // Create new document object based on data model
          const newDocument = {
            doc_type: docType,
            doc_path: docRef,
            doc_title: docTitle,
            doc_upload_date: new Date().toISOString(),
            doc_upload_by: docUploadBy,
            id: uuidv4(),
          };

          return {
            ...jCode, // Copy the whole j_code element
            documents: [
              ...(jCode.documents || []), // Preserve existing documents
              newDocument, // Add new document
            ],
          };
        }
        return jCode;
      }),
    };
  });
  return updatedJCodes;
};

/**
 * Removes the auth letter document with the given doc_path from the specified J-codes.
 * Used when replacing an incorrect letter: remove the old doc, then add the new one.
 *
 * @param authEntries - Array of auth range responses containing J-codes
 * @param jCodeIds - J-code IDs to update (remove the doc from these only)
 * @param docPath - doc_path of the auth letter document to remove
 * @returns Updated array of auth range responses
 */
export const removeAuthLetterDocByPath = (
  authEntries: AuthRangeResponse[],
  jCodeIds: string[],
  docPath: string,
): AuthRangeResponse[] =>
  authEntries.map((authEntry) => ({
    ...authEntry,
    j_codes: authEntry.j_codes?.map((jCode) => {
      if (!jCodeIds.includes(jCode.id ?? "")) return jCode;
      return {
        ...jCode,
        auth_letter_review_status: null,
        documents:
          jCode.documents?.filter(
            (doc) =>
              !(
                doc?.doc_type === DocumentTypesForOrder.AUTH_LETTER &&
                doc?.doc_path === docPath
              ),
          ) ?? [],
      };
    }),
  }));

/**
 * Checks whether any document across all auth entries has the given `id`.
 *
 * @param authEntries - Array of auth range responses containing J-codes
 * @param documentId - The document `id` to search for
 * @returns `true` if a document with the given `id` exists
 */
const hasDocumentWithId = (
  authEntries: AuthRangeResponse[],
  documentId: string,
): boolean =>
  authEntries.some((entry) =>
    entry.j_codes?.some((jCode) =>
      jCode.documents?.some((doc) => doc.id === documentId),
    ),
  );

/**
 * Removes a specific document by matching its own `id`.
 *
 * Iterates through all auth entries and their J-codes, filtering out the
 * document whose `id` matches the given `documentId`.
 *
 * @param authEntries - Array of auth range responses containing J-codes
 * @param documentId - The `id` of the document (within `JCodeDocuments`) to remove
 * @returns Updated array of auth range responses with the matching document removed
 */
const removeDocumentById = (
  authEntries: AuthRangeResponse[],
  documentId: string,
) =>
  authEntries.map((authEntry) => ({
    ...authEntry,
    j_codes: authEntry.j_codes?.map((jCode) => {
      const removedDoc = jCode.documents?.find((d) => d.id === documentId);
      const isAuthLetter =
        removedDoc?.doc_type === DocumentTypesForOrder.AUTH_LETTER;
      return {
        ...jCode,
        ...(isAuthLetter ? { auth_letter_review_status: null } : {}),
        documents:
          jCode.documents?.filter((doc) => doc.id !== documentId) || [],
      };
    }),
  }));

/**
 * Removes all documents of a given type from a specific J-code.
 *
 * Finds the J-code whose `id` matches the given `identifier`, then filters
 * out every document whose `doc_type` matches `docType`.
 *
 * @param authEntries - Array of auth range responses containing J-codes
 * @param identifier - The J-code `id` to match
 * @param docType - The document type to remove
 * @returns Updated array of auth range responses with the matching documents removed
 */
const removeDocumentByJCodeAndType = (
  authEntries: AuthRangeResponse[],
  identifier: string,
  docType: string,
) =>
  authEntries.map((authEntry) => ({
    ...authEntry,
    j_codes: authEntry.j_codes?.map((jCode) => {
      if (jCode.id !== identifier) return jCode;
      const isAuthLetter = docType === DocumentTypesForOrder.AUTH_LETTER;
      return {
        ...jCode,
        ...(isAuthLetter ? { auth_letter_review_status: null } : {}),
        documents:
          jCode.documents?.filter((doc) => doc.doc_type !== docType) || [],
      };
    }),
  }));

/**
 * Removes a document from auth entries using a two-phase lookup.
 *
 * First attempts to find and remove a document whose own `id` matches the
 * given `identifier`. If no such document exists, falls back to treating
 * `identifier` as a J-code ID and removes all documents of the specified
 * `docType` from that J-code.
 *
 * @param authEntries - Array of auth range responses containing J-codes
 * @param identifier - Either a document `id` or a J-code `id`
 * @param docType - The document type to remove (used only in the fallback path)
 * @returns Updated array of auth range responses with the target document(s) removed
 */
export const removeDocumentFromJcode = (
  authEntries: AuthRangeResponse[],
  identifier: string,
  docType: string,
) => {
  if (hasDocumentWithId(authEntries, identifier)) {
    return removeDocumentById(authEntries, identifier);
  }
  return removeDocumentByJCodeAndType(authEntries, identifier, docType);
};

/**
 * Updates the title of a single document.
 * @param document - The document object to update
 * @param docTitle - The new title for the document
 * @returns A new document object with the updated title
 */
export const updateSingleDocumentTitle = (
  document: JCodeDocuments,
  docTitle: string,
) => {
  return {
    ...document,
    doc_title: docTitle,
  };
};

/**
 * Updates the title of a specific document within an array of J-code documents.
 * @param documents - Array of J-code documents
 * @param docId - The ID of the document to update
 * @param docTitle - The new title for the document
 * @returns Updated array of documents with the matching document's title updated
 */
export const updateDocumentFromJCodeDocumentsArrayAndUpdateTitle = (
  documents: JCodeDocuments[],
  docId: string,
  docTitle: string,
) => {
  return documents.map((doc) =>
    doc.id === docId ? updateSingleDocumentTitle(doc, docTitle) : doc,
  );
};

/**
 * Updates a document's title within the J-codes of auth entries by finding the document by ID.
 * @param authEntries - Array of auth range responses containing J-codes
 * @param docId - The ID of the document to update
 * @param docTitle - The new title for the document
 * @returns Updated array of auth range responses with the document title updated
 */
export const updateJCodeDocumentWithNewDocumentTitle = (
  authEntries: AuthRangeResponse[],
  docId: string,
  docTitle: string,
) => {
  const updatedJCodes = authEntries.map((authEntry) => {
    return {
      ...authEntry,
      j_codes: authEntry.j_codes?.map((jCode) => {
        if (jCode.documents?.some((document) => document.id === docId)) {
          return {
            ...jCode,
            documents: updateDocumentFromJCodeDocumentsArrayAndUpdateTitle(
              jCode.documents ?? [],
              docId,
              docTitle,
            ),
          };
        }
        return jCode;
      }),
    };
  });
  return updatedJCodes;
};
